import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, ExternalLink, Lock } from "lucide-react";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { sanitizeHtml } from "@/lib/sanitize";
interface Partnership {
  id: string;
  name: string;
  image_url: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  discount_code: string | null;
  redemption_url: string | null;
  category: string | null;
}

export default function PartnerDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, isSubscriber } = useAuth();
  const { toast } = useToast();
  const [partnership, setPartnership] = useState<Partnership | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const hasAccess = user && isSubscriber;

  useEffect(() => {
    if (!user) {
      navigate("/auth/login");
      return;
    }
    if (!isSubscriber) {
      return;
    }
    if (id) {
      fetchPartnership();
    }
  }, [user, isSubscriber, id]);

  const fetchPartnership = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("partnerships")
      .select("*")
      .eq("id", id)
      .eq("is_active", true)
      .single();

    if (error || !data) {
      setNotFound(true);
    } else {
      setPartnership(data);
    }
    setLoading(false);
  };

  const getDescription = (p: Partnership) => {
    const lang = i18n.language;
    let description = "";
    if (lang === "en" && p.description_en) description = p.description_en;
    else if (lang === "es" && p.description_es) description = p.description_es;
    else description = p.description_it || "";
    return sanitizeHtml(description);
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: t("partners.codeCopied", "Codice copiato!") });
  };

  // No access view
  if (!hasAccess) {
    return (
      <>
        <SEO
          title={t("partners.seoTitle", "Partners - Inclusia")}
          description={t("partners.seoDescription", "Scopri le partnership esclusive per i nostri abbonati")}
        />
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="flex-1 bg-surface py-16">
            <div className="max-w-2xl mx-auto px-4 text-center">
              <BrutalCard className="p-12">
                <Lock className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                <h1 className="font-heading text-3xl font-bold mb-4">
                  {t("partners.accessDenied", "Accesso Riservato")}
                </h1>
                <p className="text-muted-foreground mb-6">
                  {t(
                    "partners.subscriptionRequired",
                    "Questa sezione è riservata agli abbonati. Sottoscrivi un piano per accedere alle partnership esclusive."
                  )}
                </p>
                <BrutalButton onClick={() => navigate("/pricing")}>
                  {t("partners.viewPlans", "Scopri i Piani")}
                </BrutalButton>
              </BrutalCard>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={partnership ? `${partnership.name} - Partners` : t("partners.seoTitle", "Partners - Inclusia")}
        description={t("partners.seoDescription", "Scopri le partnership esclusive per i nostri abbonati")}
      />
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-1 bg-surface py-12">
          <div className="max-w-4xl mx-auto px-4">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t("common.loading", "Caricamento...")}</p>
              </div>
            ) : notFound || !partnership ? (
              <BrutalCard className="p-12 text-center">
                <h1 className="font-heading text-2xl font-bold mb-4">
                  {t("partners.notFound", "Partnership non trovata")}
                </h1>
                <p className="text-muted-foreground mb-6">
                  {t("partners.notFoundDescription", "La partnership richiesta non esiste o non è più disponibile.")}
                </p>
                <BrutalButton onClick={() => navigate("/partners")}>
                  {t("partners.backToPartners", "Torna ai Partners")}
                </BrutalButton>
              </BrutalCard>
            ) : (
              <BrutalCard className="overflow-hidden p-6 md:p-8">
                <div className="flex flex-col gap-6 md:gap-8">
                  {/* Header a due colonne */}
                  <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                    {/* Colonna sinistra: Breadcrumb + Titolo */}
                    <div className="flex flex-col justify-center order-2 md:order-1">
                      <SEOBreadcrumb 
                        items={[
                          { label: t('partners.breadcrumb', 'Partners'), href: '/partners' },
                          { label: partnership.name }
                        ]} 
                        align="left"
                      />
                      <h1 className="font-heading font-bold text-3xl md:text-4xl text-dark mt-4 mb-3">
                        {partnership.name}
                      </h1>
                      {partnership.category && (
                        <BrutalBadge variant="secondary" className="w-fit">
                          {t(`partners.categories.${partnership.category}`, partnership.category)}
                        </BrutalBadge>
                      )}
                    </div>
                    
                    {/* Colonna destra: Immagine */}
                    <div className="aspect-[4/3] bg-muted order-1 md:order-2 rounded-sm overflow-hidden border-[3px] border-dark">
                      {partnership.image_url ? (
                        <img
                          src={partnership.image_url}
                          alt={partnership.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-8xl">🤝</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contenuto sotto */}
                  <div>
                    {/* Description */}
                    <div
                      className="prose prose-lg max-w-none mb-8 
                                 prose-headings:font-heading prose-headings:font-bold prose-headings:text-foreground
                                 prose-p:text-foreground prose-p:leading-relaxed
                                 prose-a:text-primary prose-a:font-bold prose-a:no-underline hover:prose-a:underline
                                 prose-strong:font-bold prose-strong:text-foreground
                                 prose-ul:list-disc prose-ol:list-decimal
                                 prose-li:text-foreground"
                      dangerouslySetInnerHTML={{ __html: getDescription(partnership) }}
                    />

                    {/* Discount code */}
                    {partnership.discount_code && (
                      <div className="mb-6 p-4 bg-primary/10 border-[3px] border-dark">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">
                              {t("partners.discountCode", "Codice Sconto")}
                            </p>
                            <code className="font-mono font-bold text-2xl">
                              {partnership.discount_code}
                            </code>
                          </div>
                          <BrutalButton
                            variant="secondary"
                            onClick={() => handleCopyCode(partnership.discount_code!)}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            {t("partners.copy", "Copia")}
                          </BrutalButton>
                        </div>
                      </div>
                    )}

                    {/* CTA */}
                    {partnership.redemption_url && (
                      <BrutalButton
                        size="lg"
                        onClick={async () => {
                          // Track click
                          await supabase.functions.invoke('track-partnership-click', {
                            body: { partnership_id: partnership.id }
                          });
                          window.open(partnership.redemption_url!, "_blank");
                        }}
                        className="w-full sm:w-auto"
                      >
                        <ExternalLink className="h-5 w-5 mr-2" />
                        {t("partners.redeem", "Riscatta Offerta")}
                      </BrutalButton>
                    )}
                  </div>
                </div>
              </BrutalCard>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
